import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notificationsApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Bell,
  Check,
  FolderKanban,
  ListTodo,
  MessageSquare,
  CreditCard,
  CheckCheck,
  Eye,
  EyeOff,
  ChevronRight,
} from "lucide-react";

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRead, setShowRead] = useState(false);

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsApi.getAll();
      setNotifications(response.data);
    } catch (error) {
      console.error("Error fetching notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (e, id) => {
    e.stopPropagation();
    try {
      await notificationsApi.markRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllRead();
      toast.success("All marked as read");
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (error) {
      toast.error("Failed to mark all as read");
    }
  };

  const handleClick = (notification) => {
    if (!notification.read) {
      notificationsApi.markRead(notification.id).catch(() => {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === notification.id ? { ...n, read: true } : n))
      );
    }
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const getIcon = (type) => {
    const cls = "h-4 w-4";
    switch (type) {
      case "project":
      case "project_assigned":
      case "project_new":
        return <FolderKanban className={cls} />;
      case "task":
      case "task_assigned":
      case "task_status":
      case "task_review":
      case "task_approved":
      case "task_revision":
        return <ListTodo className={cls} />;
      case "comment":
        return <MessageSquare className={cls} />;
      case "payment":
        return <CreditCard className={cls} />;
      default:
        return <Bell className={cls} />;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case "project":
      case "project_assigned":
      case "project_new":
        return "text-sky-400 bg-sky-500/10";
      case "task":
      case "task_assigned":
      case "task_status":
      case "task_review":
      case "task_approved":
      case "task_revision":
        return "text-orange-400 bg-orange-500/10";
      case "comment":
        return "text-purple-400 bg-purple-500/10";
      case "payment":
        return "text-green-400 bg-green-500/10";
      default:
        return "text-zinc-400 bg-zinc-800";
    }
  };

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  const unreadNotifications = notifications.filter((n) => !n.read);
  const readNotifications = notifications.filter((n) => n.read);
  const displayedNotifications = showRead ? notifications : unreadNotifications;
  const unreadCount = unreadNotifications.length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="notifications-page" className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-xl font-bold text-zinc-50 font-['Manrope']">
            Notifications
          </h1>
          {unreadCount > 0 && (
            <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30 text-xs">
              {unreadCount} unread
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "text-xs h-8 gap-1.5",
              showRead
                ? "text-orange-400 hover:text-orange-300"
                : "text-zinc-500 hover:text-zinc-300"
            )}
            onClick={() => setShowRead(!showRead)}
            data-testid="toggle-read-filter"
          >
            {showRead ? (
              <Eye className="h-3.5 w-3.5" />
            ) : (
              <EyeOff className="h-3.5 w-3.5" />
            )}
            {showRead ? "Showing all" : "Show read"}
          </Button>

          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs h-8 gap-1.5 text-zinc-500 hover:text-zinc-300"
              onClick={handleMarkAllRead}
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="h-3.5 w-3.5" />
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Notifications List */}
      {displayedNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Bell className="h-10 w-10 text-zinc-700 mb-3" />
          <p className="text-zinc-500 text-sm">
            {showRead
              ? "No notifications yet"
              : unreadCount === 0 && readNotifications.length > 0
              ? "All caught up!"
              : "No notifications yet"}
          </p>
          {!showRead && readNotifications.length > 0 && (
            <button
              className="text-xs text-zinc-600 hover:text-zinc-400 mt-2 underline underline-offset-2"
              onClick={() => setShowRead(true)}
            >
              Show {readNotifications.length} read notification
              {readNotifications.length !== 1 ? "s" : ""}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-1">
          {displayedNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleClick(notification)}
              className={cn(
                "group flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all duration-150",
                notification.read
                  ? "opacity-50 hover:opacity-70 hover:bg-zinc-800/30"
                  : "bg-zinc-800/40 hover:bg-zinc-800/60 border-l-2 border-l-orange-500"
              )}
              data-testid={`notification-${notification.id}`}
            >
              {/* Icon */}
              <div
                className={cn(
                  "flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center",
                  getIconColor(notification.type)
                )}
              >
                {getIcon(notification.type)}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-sm leading-tight truncate",
                    notification.read ? "text-zinc-500" : "text-zinc-200 font-medium"
                  )}
                >
                  {notification.title}
                </p>
                <p className="text-xs text-zinc-600 truncate mt-0.5">
                  {notification.message}
                </p>
              </div>

              {/* Time + Actions */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="text-[11px] text-zinc-600 whitespace-nowrap">
                  {timeAgo(notification.created_at)}
                </span>

                {!notification.read ? (
                  <button
                    onClick={(e) => handleMarkRead(e, notification.id)}
                    className="p-1 rounded hover:bg-zinc-700/50 text-zinc-500 hover:text-green-400 transition-colors"
                    title="Mark as read"
                    data-testid={`mark-read-${notification.id}`}
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  notification.link && (
                    <ChevronRight className="h-3.5 w-3.5 text-zinc-700 group-hover:text-zinc-500 transition-colors" />
                  )
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationsPage;
